/**
 * Conference Room Occupation Logic
 * Handles occupation status calculation and booking slot generation
 */

import { extractFullTime } from "./conferenceRoomTime";

const SOON_OCCUPIED_THRESHOLD_MINUTES = 30;

/**
 * Finds the next booking that hasn't ended yet
 * @param {array} bookings - Array of booking objects
 * @param {string} currentTime - Current time in HH:MM:SS format
 * @returns {object|null} Next booking or null
 */
export function findNextBooking(bookings, currentTime) {
    const upcomingBookings = bookings.filter(
        (booking) => extractFullTime(booking.end) > currentTime
    );
    return upcomingBookings.length > 0 ? upcomingBookings[0] : null;
}

/**
 * Determines the occupation status based on current time and next booking
 * @param {object|null} nextBooking - Next booking object
 * @param {string} currentTime - Current time in HH:MM:SS format
 * @returns {string} Occupation status: 'free', 'occupied', or 'soonOccupied'
 */
export function calculateOccupationStatus(nextBooking, currentTime) {
    if (!nextBooking) {
        return "free";
    }

    const bookingStartTime = extractFullTime(nextBooking.start);
    const bookingEndTime = extractFullTime(nextBooking.end);

    // Check if currently occupied
    if (currentTime > bookingStartTime && currentTime < bookingEndTime) {
        return "occupied";
    }

    // Check if booking is within 30 minutes
    const currentHour = parseInt(currentTime.slice(0, 2));
    const currentMinute = parseInt(currentTime.slice(3, 5));
    const bookingStartHour = parseInt(bookingStartTime.slice(0, 2));
    const bookingStartMinute = parseInt(bookingStartTime.slice(3, 5));

    const hourDifference = bookingStartHour - currentHour;
    const minuteDifference = bookingStartMinute - currentMinute;

    // Within same hour and within 30 minutes
    if (hourDifference === 0 && minuteDifference >= -SOON_OCCUPIED_THRESHOLD_MINUTES && minuteDifference <= SOON_OCCUPIED_THRESHOLD_MINUTES) {
        return "soonOccupied";
    }

    // Next hour, but close to the booking time
    if (hourDifference === 1 && minuteDifference < -SOON_OCCUPIED_THRESHOLD_MINUTES) {
        return "soonOccupied";
    }

    return "free";
}

/**
 * Generates occupation slots including available time slots between bookings
 * @param {array} bookings - Array of booking objects
 * @param {object|null} nextBooking - Next booking object
 * @param {string} currentTime - Current time in HH:MM:SS format
 * @returns {array} Array of time slots with availability information
 */
export function generateOccupationSlots(bookings, nextBooking, currentTime) {
    const slots = [];

    if (!nextBooking) {
        return slots;
    }

    for (let i = 0; i < bookings.length; i++) {
        const currentBooking = bookings[i];
        slots.push(currentBooking);

        // Add available slot between bookings
        if (i < bookings.length - 1) {
            const nextBookingInList = bookings[i + 1];
            if (nextBookingInList.start !== currentBooking.end) {
                slots.push({
                    status: "available",
                    start: currentBooking.end,
                    end: nextBookingInList.start,
                });
            }
        }

        // Mark current booking
        const bookingStartTime = extractFullTime(nextBooking.start);
        const bookingEndTime = extractFullTime(nextBooking.end);
        if (currentTime > bookingStartTime && currentTime < bookingEndTime) {
            slots[0].isCurrentBooking = true;
        }
    }

    return slots;
}

/**
 * Gets CSS classes for occupation status indicator
 * @param {string} status - Occupation status
 * @returns {object} Object with CSS class flags
 */
export function getOccupationStatusClasses(status) {
    return {
        'bg-room-green': status === 'free',
        'bg-room-red': status === 'occupied',
        'bg-yellow-400': status === 'soonOccupied',
    };
}
